import { Directive, OnInit, WritableSignal, effect, inject, input, output, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { OnActionEvent, PaginationBase } from '../../models/pagination.abstract';
import { IFilterQueryResponse, ListFilterBarOptions } from '../../models/filter.models';
import { EntityCommunicationService } from '../../services/entity-cominication.service';

@Directive()
export abstract class EntityBaseListComponent<T> extends PaginationBase implements OnInit {
  // Services
  protected override router = inject(Router);
  protected override route = inject(ActivatedRoute);

  // Abstract properties to be implemented by child classes
  protected abstract entityCommunicationService: EntityCommunicationService<T>;
  public abstract getCustomButtons(item: T): MenuItem[];

  // Inputs & Outputs
  readonly initialViewType = input<'table' | 'card'>('card');
  readonly onlyView = input<boolean>(false);
  // Outputs
  readonly onSelect = output<T>();

  // Writable Signals for component state
  public items: WritableSignal<T[]> = signal([]);
  public viewType: WritableSignal<'table' | 'card'>;
  public totalRecordsSignal: WritableSignal<number> = signal(0);

  // Public properties for configuration
  public filterBarOptions: ListFilterBarOptions = { showActions: true, showCreateButton: true, showViewButton: true };
  public columns: any[] = [];

  constructor() {
    super();
    this.viewType = signal(this.initialViewType());
    effect(() => {
      // Keep the parent's totalRecords in sync with our signal
      this.totalRecords = this.totalRecordsSignal();
    });
  }

  async ngOnInit(): Promise<void> {
    await this.loadData();
  }

  protected override async loadData(): Promise<void> {
    try {
      this.isLoading = true;
      const response: IFilterQueryResponse<T> = await this.entityCommunicationService.query(this.filterConfig);
      this.items.set(response.rows);

      this.totalRecordsSignal.set(response.count);
    } catch (error) {
      console.error('Error loading data', error);
    } finally {
      this.isLoading = false;
    }
  }

  public onNew(): void {
    this.router.navigate(['./edit'], { relativeTo: this.route });
  }

  public toggleView(): void {
    this.viewType.update((v) => (v === 'card' ? 'table' : 'card'));
  }

  public override async doAction(actionEvent: OnActionEvent): Promise<void> {
    const { item, action } = actionEvent;

    if (!item) {
      return;
    }

    switch (action) {
      case 'view':
        this.router.navigate(['./details', item._id], { relativeTo: this.route });
        break;
      case 'edit':
        this.router.navigate(['./edit', item._id], { relativeTo: this.route });
        break;
      case 'delete':
        const confirmed = confirm(`Are you sure you want to delete this item?`);
        if (confirmed) {
          try {
            await this.entityCommunicationService.remove(item._id);
          } catch (error) {
            console.error('Error deleting item', error);
          } finally {
            await this.loadData(); // Refresh list
          }
        }
        break;
      case 'clone':
        await this.entityCommunicationService.clone(item._id);
        await this.loadData(); // Refresh list
        break;
      default:
      // this.handleCustomAction(actionEvent);
    }
  }
}
